import type {
  CreateOrderRequest,
  ModifyOrderRequest,
  Order,
  OrderQueryParams,
  OrdersListResponse,
} from "@/types/execution.types";
import {
  API_BASE_URL,
  buildQueryString,
  fetchWithTimeout,
  handleResponse,
} from "./shared";

// ============================================================================
// Orders API (CQG Integration)
// ============================================================================

export const ordersApi = {
  /**
   * List orders with optional filters
   */
  async list(params?: OrderQueryParams): Promise<OrdersListResponse> {
    const queryParams = params
      ? {
          symbol: params.symbol,
          side: params.side,
          status: params.status,
          brokerId: params.brokerId,
          accountId: params.accountId,
          strategyId: params.strategyId,
          limit: params.limit?.toString(),
          offset: params.offset?.toString(),
        }
      : undefined;

    const queryString = buildQueryString(queryParams);
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/orders${queryString}`
    );
    return handleResponse<OrdersListResponse>(response);
  },

  /**
   * Get a single order by ID
   */
  async get(orderId: string): Promise<Order> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/orders/${orderId}`
    );
    return handleResponse<Order>(response);
  },

  /**
   * Create a new order
   */
  async create(data: CreateOrderRequest): Promise<Order> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/cqg/orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<Order>(response);
  },

  /**
   * Modify an existing order
   */
  async modify(orderId: string, data: ModifyOrderRequest): Promise<Order> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/orders/${orderId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<Order>(response);
  },

  /**
   * Cancel an order
   */
  async cancel(orderId: string): Promise<Order> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/orders/${orderId}`,
      {
        method: "DELETE",
      }
    );
    return handleResponse<Order>(response);
  },

  /**
   * Bulk cancel orders
   */
  async bulkCancel(params?: {
    symbol?: string;
    side?: string;
    accountId?: string;
    cancelAll?: boolean;
  }): Promise<{ canceledCount: number; orders: Order[] }> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/orders/bulk-cancel`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params || {}),
      }
    );
    return handleResponse<{ canceledCount: number; orders: Order[] }>(response);
  },
};
